import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { EmbeddedContentApiResponse } from '@shared/api/embededContent.api';
import { getApiContentUri, getApiEmbeddedContentUri } from '@shared/api/embededContent.api';
import type { RootState } from '@store/store.ts';
import { getExpiresAt, isContentEntryValid } from '@utils/embededContents.ts';

export type ContentEntry = {
    tempUrl: string;
    expiresAt: number | null;
    isLoading: boolean;
    error: any | null;
};

interface ContentAssetsState {
    contentByEmbeddedUrl: Record<string, ContentEntry>;
    contentByIdUrl: Record<string, ContentEntry>;
}

const initialState: ContentAssetsState = {
    contentByEmbeddedUrl: {},
    contentByIdUrl: {},
};

export const resolveContentUri = createAsyncThunk<
    EmbeddedContentApiResponse,
    string,
    { state: RootState; rejectValue: any }
>(
    'content/resolveContentUri',
    (id, { rejectWithValue }) =>
        getApiContentUri(id)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
    {
        condition: (id, { getState }) => {
            const entry = getState().contentAssets.contentByIdUrl[id];

            return !entry?.isLoading && !isContentEntryValid(entry);
        },
    },
);

export const resolveEmbeddedContentUri = createAsyncThunk<
    EmbeddedContentApiResponse,
    string,
    { state: RootState; rejectValue: any }
>(
    'content/resolveEmbeddedContentUri',
    (id, { rejectWithValue }) =>
        getApiEmbeddedContentUri(id)
            .then((res) => res)
            .catch((err) => rejectWithValue(err)),
    {
        condition: (id, { getState }) => {
            const entry = getState().contentAssets.contentByEmbeddedUrl[id];

            return !entry?.isLoading && !isContentEntryValid(entry);
        },
    },
);

const contentSlice = createSlice({
    name: 'contents',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(resolveEmbeddedContentUri.pending, (state, action) => {
                const tempUrl = action.meta.arg;
                state.contentByEmbeddedUrl[tempUrl] = {
                    tempUrl: '',
                    expiresAt: null,
                    isLoading: true,
                    error: null,
                };
            })
            .addCase(resolveEmbeddedContentUri.fulfilled, (state, action) => {
                const tempUrl = action.meta.arg;

                state.contentByEmbeddedUrl[tempUrl] = {
                    tempUrl: action.payload.url,
                    expiresAt: getExpiresAt(action.payload.contentMinutesDuration),
                    isLoading: false,
                    error: null,
                };
            })
            .addCase(resolveEmbeddedContentUri.rejected, (state, action) => {
                const tempUrl = action.meta.arg;
                state.contentByEmbeddedUrl[tempUrl] = {
                    tempUrl: '',
                    expiresAt: null,
                    isLoading: false,
                    error: action.payload ?? action.error,
                };
            })

            .addCase(resolveContentUri.pending, (state, action) => {
                const id = action.meta.arg;
                state.contentByIdUrl[id] = {
                    tempUrl: '',
                    expiresAt: null,
                    isLoading: true,
                    error: null,
                };
            })
            .addCase(resolveContentUri.fulfilled, (state, action) => {
                const id = action.meta.arg;

                state.contentByIdUrl[id] = {
                    tempUrl: action.payload.url,
                    expiresAt: getExpiresAt(action.payload.contentMinutesDuration),
                    isLoading: false,
                    error: null,
                };
            })
            .addCase(resolveContentUri.rejected, (state, action) => {
                const id = action.meta.arg;
                state.contentByIdUrl[id] = {
                    tempUrl: '',
                    expiresAt: null,
                    isLoading: false,
                    error: action.payload ?? action.error,
                };
            });
    },
});

export default contentSlice.reducer;
